import { useNotificationActions } from '@ltvco/refresh-lib/hooks';
import { IconButton, Stack, Text, Menu, Box } from '@ltvco/refresh-lib/theme';
import {
  NotificationContent,
  ClearAllNotificationsModal,
  useSriracha,
} from '@ltvco/refresh-lib/v2';
import {
  Notifications as Bell,
  CleaningServices,
  DoneAll,
  MoreVert,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';

import NoNotificationsBanner from './NoNotificationsBanner';
import NotificationsLoading from './NotificationsLoading';
import { StyledMenuItem, StyledSwitchLabel } from './styledComponents';

const PER_PAGE = 6;

export const Notifications = () => {
  const [anchorElMarkAll, setAnchorElMarkAll] = useState<null | HTMLElement>(
    null
  );
  const [isClearAllModalOpen, setIsClearAllModalOpen] = useState(false);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [showUnread, setShowUnread] = useState(false);

  const {
    data: notifications,
    readNotification: onReadNotification,
    readAllNotifications: onReadAllNotifications,
    deleteNotification: onClearNotification,
    currentPage,
    totalPages: maxPage,
    nextPage: onClickNext,
    prevPage: onClickPrev,
    jumpToPage,
    isLoading,
  } = useSriracha(PER_PAGE);

  const {
    normalizedNotifications,
    filteredNotifications,
    onNotificationClick,
  } = useNotificationActions({
    notifications,
  });

  const displayedNotifications = showUnread
    ? filteredNotifications?.filter((n) => !n.read)
    : filteredNotifications;

  const handleMenuClickMarkAll = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorElMarkAll(event.currentTarget);
    setMenuIsOpen(true);
  };

  const handleCloseMenuMarkAll = () => {
    setMenuIsOpen(false);
    setAnchorElMarkAll(null);
  };

  const handleMarkAllAsRead = () => {
    onReadAllNotifications.mutate();
    for (const notification of normalizedNotifications) {
      notification.read = true;
    }
    handleCloseMenuMarkAll();
  };

  const handleOpenClearAllModal = () => {
    setIsClearAllModalOpen(true);
    handleCloseMenuMarkAll();
  };

  const handleClearAllNotifications = () => {
    if (!onClearNotification) return;
    for (const notification of notifications || []) {
      onClearNotification.mutate(notification.id);
    }
  };

  useEffect(() => {
    if (maxPage >= 1 && currentPage !== 1) {
      jumpToPage(1);
    }
  }, [showUnread]);

  return (
    <Stack direction="column" padding={{ xs: 2.5, sm: 0 }}>
      <Stack
        spacing={2}
        mt={2}
        pr={2}
        direction="row"
        justifyContent="space-between"
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Bell sx={{ fontSize: '1.25rem' }} />
          <Text variant="h3">Notifications</Text>
        </Stack>

        <>
          <IconButton onClick={handleMenuClickMarkAll}>
            <MoreVert color="inherit" sx={{ transform: 'rotate(90deg)' }} />
          </IconButton>

          <Menu
            anchorEl={anchorElMarkAll}
            open={menuIsOpen}
            onClose={handleCloseMenuMarkAll}
          >
            <StyledMenuItem onClick={handleMarkAllAsRead}>
              <DoneAll fontSize="small" />
              <Text>Mark all as read</Text>
            </StyledMenuItem>

            {!!displayedNotifications.length && (
              <StyledMenuItem onClick={handleOpenClearAllModal}>
                <CleaningServices fontSize="small" />
                <Text>Clear all notifications</Text>
              </StyledMenuItem>
            )}
          </Menu>
        </>
      </Stack>

      {filteredNotifications?.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '16px 0',
          }}
        >
          <Text color="text.secondary" variant="body1">
            Show unread only
          </Text>
          <StyledSwitchLabel
            label=""
            checked={showUnread}
            setChecked={() => setShowUnread(!showUnread)}
          />
        </Box>
      )}

      {isLoading ? (
        <NotificationsLoading count={PER_PAGE} />
      ) : (
        <>
          {!(displayedNotifications.length > 0) ? (
            <NoNotificationsBanner />
          ) : (
            <NotificationContent
              isOnNotificationPage
              filteredNotifications={displayedNotifications}
              onNotificationClick={onNotificationClick}
              onReadNotification={onReadNotification.mutate}
              isLoading={isLoading}
              showUnread={showUnread}
              pagination={{
                currentPage,
                maxPage,
                onClickPrev,
                onClickNext,
              }}
            />
          )}
        </>
      )}

      <ClearAllNotificationsModal
        isOpen={isClearAllModalOpen}
        setIsOpen={setIsClearAllModalOpen}
        clearAllNotifications={handleClearAllNotifications}
      />
    </Stack>
  );
};
